import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Search,
  LayoutGrid,
  Scale,
  Plug,
  Settings2,
  GraduationCap,
  Rocket,
  TrendingUp,
} from "lucide-react";
import { PageHero, SectionWrapper, SectionHeader } from "../components/Section";

const STEPS = [
  {
    n: "01",
    icon: Search,
    title: "Institutional Understanding",
    body: [
      "Allinstra starts by understanding the institution’s operating environment, departments, workflows, documents, services, reporting needs, current systems, and priority challenges.",
      "This establishes the foundation for a realistic and value-driven project scope.",
    ],
  },
  {
    n: "02",
    icon: LayoutGrid,
    title: "Scope and Module Architecture",
    body: [
      "Relevant product families and modules are selected according to institutional priorities, legal environment, technical conditions, operational capacity, and implementation readiness.",
      "The goal is to define a scope that is useful, realistic, and aligned with the institution’s immediate and future needs.",
    ],
  },
  {
    n: "03",
    icon: Scale,
    title: "Local Regulatory and Infrastructure Review",
    body: [
      "Some modules depend on local legislation, public infrastructure, e-government services, payment systems, identity or address registries, electronic signature availability, land registry access, GIS infrastructure, or third-party systems.",
      "This review helps define what is possible, what requires adaptation, and what should be planned for later phases.",
    ],
  },
  {
    n: "04",
    icon: Plug,
    title: "Integration Feasibility",
    body: [
      "Where integrations are required, Allinstra evaluates data sources, APIs, permissions, security requirements, technical documentation, data quality, third-party dependencies, and institutional readiness.",
      "This supports more reliable planning and reduces the risk of unrealistic integration assumptions.",
    ],
  },
  {
    n: "05",
    icon: Settings2,
    title: "Configuration and Localization",
    body: [
      "Selected modules are configured around institutional terminology, workflows, user roles, authorization rules, document formats, reporting needs, forms, and local operating practices.",
      "Localization may include language, process logic, legal references, document structures, and country-specific adjustments.",
    ],
  },
  {
    n: "06",
    icon: GraduationCap,
    title: "Training and Pilot Use",
    body: [
      "Pilot use allows the institution to test selected workflows, validate assumptions, collect user feedback, refine configuration, and prepare internal teams before broader rollout.",
      "This creates stronger adoption and reduces implementation friction.",
    ],
  },
  {
    n: "07",
    icon: Rocket,
    title: "Go-Live and Operational Support",
    body: [
      "After configuration, testing, and training, the system moves into go-live according to the approved implementation plan.",
      "Support may include user assistance, issue tracking, configuration refinement, operational guidance, and stabilization after launch.",
    ],
  },
  {
    n: "08",
    icon: TrendingUp,
    title: "Continuous Improvement",
    body: [
      "Institutional systems should evolve as processes, laws, integrations, reporting needs, and organizational priorities change.",
      "Allinstra supports phased improvement, additional module activation, workflow optimization, reporting refinement, and future integration planning where appropriate.",
    ],
  },
];

const Implementation = () => (
  <main data-testid="page-implementation">
    <PageHero
      overline="Implementation"
      title="From product capability to institutional value."
      lead="Allinstra implementation is designed to move institutions from product interest to practical operational value."
    >
      <p className="max-w-3xl text-[15px] text-ink-600 leading-relaxed">
        Each project begins with institutional understanding and continues through scope
        definition, localization, integration review, configuration, training, pilot use,
        go-live, and continuous improvement. The objective is not only to deploy software, but
        to create a digital structure that the institution can actually adopt, operate, and
        improve over time.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/solutions" data-testid="implementation-hero-solutions" className="btn-primary">
          Explore Solutions <ArrowUpRight size={16} />
        </Link>
        <Link to="/contact" data-testid="implementation-hero-discuss" className="btn-secondary">
          Discuss Implementation <ArrowRight size={14} />
        </Link>
      </div>
    </PageHero>

    <SectionWrapper id="implementation-approach">
      <SectionHeader
        overline="Implementation approach"
        title="A controlled path from assessment to adoption."
      />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {STEPS.map((s) => (
          <article
            key={s.n}
            data-testid={`implementation-step-${s.n}`}
            className="bg-white border border-ink-200 p-7 sm:p-8 card-hover flex flex-col"
          >
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 border border-ink-200 inline-flex items-center justify-center text-cyan-600">
                <s.icon size={18} strokeWidth={1.5} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-700">
                Step {s.n} / 08
              </div>
            </div>
            <h3 className="heading-display mt-5 text-xl font-semibold leading-snug">
              {s.n.replace(/^0/, "")}. {s.title}
            </h3>
            <div className="mt-3 space-y-3 text-[14.5px] text-ink-600 leading-relaxed">
              {s.body.map((p, idx) => (
                <p key={idx}>{p}</p>
              ))}
            </div>
          </article>
        ))}
      </div>
    </SectionWrapper>

    <SectionWrapper id="implementation-phased" alt>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeader
            overline="Why phased implementation matters"
            title="Complex institutions need controlled adoption."
          />
        </div>
        <div className="lg:col-span-7 space-y-5 text-[16px] leading-relaxed text-ink-700">
          <p>
            Institutional digital transformation works best when it is planned around real
            operating conditions. A phased approach helps avoid overloaded projects,
            unrealistic integration assumptions, weak user adoption, unclear ownership, and
            fragmented rollout.
          </p>
          <p className="text-ink-900">
            Allinstra’s implementation model helps institutions move from immediate operational
            priorities toward broader digital structure step by step.
          </p>
        </div>
      </div>
    </SectionWrapper>

    <SectionWrapper id="implementation-final-cta">
      <div className="border border-ink-200 p-8 sm:p-12 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="tag-overline">Final step</div>
            <h2 className="heading-display mt-3 text-2xl sm:text-3xl font-semibold">
              Plan the right implementation path for your institution.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
            <Link
              to="/product-portfolio"
              data-testid="implementation-final-portfolio"
              className="btn-primary"
            >
              View Product Portfolio <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="implementation-final-discuss"
              className="btn-secondary"
            >
              Discuss Implementation
            </Link>
          </div>
        </div>
      </div>
    </SectionWrapper>
  </main>
);

export default Implementation;
